import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useOnClickOutside from 'use-onclickoutside';
import isPropValid from '@emotion/is-prop-valid';
import { Box, lighten, Menu, MenuItem, styled, useTheme } from '@mui/material';
import {
  IconDotsVertical,
  IconUsers,
  IconVariable,
  IconWorld,
} from '@tabler/icons-react';

import {
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import {
  doesNotificationButtonRouteMeetCondition,
  getTabNameFromPath,
  getWorkflowNavbarMessage,
  isNotificationButtonRoute,
} from '../sidebar/helpers';
import ControlMenu from './controls/menu';
import CustomEventsButton from './CustomEventsButton';
import WidgetLaunchButton from './WidgetLaunchButton';
import { setStepLevelTranslationsId } from 'src/actions/workflow-builder/workflowBuilderActions';
import BackIcon from 'src/assets/images/actions-back-icon.svg';
import iconBulb from 'src/assets/images/icon-bulb.svg';
import iconPurpleBulb from 'src/assets/images/icon-purple-bulb.svg';
import { NAVBAR_V2_CLASS } from 'src/components/app/layout/useAIStudioSidePanelSignals';
import { useAIStudio } from 'src/components/global-ai-studio/AIStudioContext';
import { AIStudioIconButton } from 'src/components/global-ai-studio/AIStudioIconButton';
import { useActiveAbTestForWorkflow } from 'src/hooks/useActiveAbTestForWorkflow';
import i18n from 'src/locales';
import OrchestratorHeader from 'src/pages/orchestrator/OrchestratorHeader';
import { AbTestTopBanner } from 'src/pages/workflow-builder-edit/autonomous-agent/components/AbTestTopBanner';
import WorkflowBuilderEditHeader from 'src/pages/workflow-builder-edit/header/WorkflowBuilderEditHeader';
import {
  useGetAutoflowWorkflowOrAutoflowTemplate,
  useWorkflowBuilderMode,
} from 'src/pages/workflow-builder-edit/hooks';
import { selectUserRole } from 'src/reducers/userReducer/userReducer';
import { selectCurrentUser } from 'src/reducers/userReducer/userReducer';
import { selectIntentWorkflowId } from 'src/reducers/workflowBuilderReducer/workflowBuilderReducer';
import {
  useGetFeatureFlagsQuery,
  useGetPermissionsQuery,
} from 'src/services/dashboard-api';
import { selectIsWorkflowActive } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import {
  selectNavbarNotificationToggle,
  setGlobalWorkflowBuilderOptions,
  setNavbarNotificationToggle,
} from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { breakpoints } from 'src/styles/breakpoints';
import { Permission, Routes } from 'src/utils/enums';

type NavBarProps = {
  children?: React.ReactNode;
  currentTabOverride?: React.ReactNode;
  hideBorder?: boolean;
  isInSimulationsDetailPage?: boolean;
  onDeleteModel?: () => void;
  onGoBack?: () => void;
  onRenameModel?: () => void;
  tabs?: React.ReactNode;
  top?: string;
};

const selectDiscoverDashboardNotification = selectNavbarNotificationToggle(
  Routes.DISCOVER_DASHBOARD,
);

const Navbar = ({
  children,
  currentTabOverride,
  hideBorder,
  isInSimulationsDetailPage,
  onDeleteModel,
  onGoBack,
  onRenameModel,
  tabs,
  top,
}: NavBarProps) => {
  const { t } = useTranslation('app');
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const role = useSelector(selectUserRole);
  const { pathname, search } = useLocation();

  // Only fetch workflow data on workflow builder edit page
  const isWorkflowBuilderEditPage = pathname === Routes.WORKFLOW_BUILDER_EDIT;
  const intentWorkflowId = useSelector(selectIntentWorkflowId) ?? '';

  const { autoflowWorkflow, isLoading } =
    useGetAutoflowWorkflowOrAutoflowTemplate(
      isWorkflowBuilderEditPage ? intentWorkflowId : '',
    );

  const isNotificationVisible = useSelector(
    selectDiscoverDashboardNotification,
  );
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const { data: appPermissions = {} } = useGetPermissionsQuery();
  const isAIStudioEnabled = Boolean(appPermissions[Permission.AI_STUDIO]);
  const {
    isLoading: isAIStudioLoading,
    // Side-panel UX flag: when on, the sidebar's AI Studio trigger moves
    // here, next to account settings (see newSidebar/index.tsx).
    isSidePanelAvailable: isAIStudioNavbarUxEnabled,
    open: openAIStudio,
  } = useAIStudio();
  const isCurrentWorkflowActive = useSelector(selectIsWorkflowActive);
  const isLandingPageStyleRevampEnabled = featureFlags.includes(
    'workflow_builder_style_revamp',
  );

  const ref = useRef<HTMLDivElement | null>(null);
  // Memoize URLSearchParams to avoid creating new object on every render
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  // TODO: need a better way to do this. We use 'channel' from the landing page and 'view' from the edit page.
  const isEmailBuilderView =
    searchParams.get('view') === 'email' ||
    searchParams.get('channel') === 'email';
  const view = searchParams.get('view');

  const user = useSelector(selectCurrentUser) || {};
  const email = user?.user?.user?.email?.split('@')?.[0] || '';

  const [modalVisibility, setModalVisibility] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleModalVisibility = useCallback(() => {
    setModalVisibility(!modalVisibility);
  }, [modalVisibility]);

  useOnClickOutside(ref, () => {
    setModalVisibility(false);
  });
  const [mode] = useWorkflowBuilderMode();

  const isAutoflowWorkflow = autoflowWorkflow?.status === 'enabled';

  const { activeAbTest, isWorkflowInActiveAbTest } = useActiveAbTestForWorkflow(
    isWorkflowBuilderEditPage ? intentWorkflowId : '',
  );

  const tabName = getTabNameFromPath(pathname);
  const isRevampedWorkflowBuilderEditPage =
    Routes.WORKFLOW_BUILDER_EDIT === pathname;
  const isRevampedWorkflowBuilderPages = [
    Routes.WORKFLOW_BUILDER,
    Routes.WORKFLOW_BUILDER_EDIT,
    Routes.SOLVE_CONFIGURATION,
    Routes.SOLVE_TESTS,
    Routes.ASSIST_WORKFLOW_BUILDER,
    Routes.ASSIST_CONFIGURATION,
  ].includes(pathname as Routes);
  const isSolveInsightsPages = pathname.startsWith('/solve-insights');

  const isRevampedActionBuilderPages =
    Routes.ACTION_BUILDER === pathname ||
    Routes.ACTION_BUILDER_EDIT === pathname ||
    pathname.startsWith('/solve/action-builder');
  // new dark mode is transparent with white text and no border bottom
  const isNewDarkMode =
    pathname === Routes.SOLVE_TESTS ||
    pathname === Routes.ORCHESTRATOR ||
    pathname === Routes.AGENT_AB_TESTS ||
    (pathname === Routes.WORKFLOW_BUILDER && isLandingPageStyleRevampEnabled) ||
    (pathname === Routes.ASSIST_WORKFLOW_BUILDER &&
      isLandingPageStyleRevampEnabled);

  // light mode for all workflow builders, but respect isNewDarkMode for other routes
  const isDarkMode = isNewDarkMode;

  // classic doesn't matter for voice workflows but it does exist so we have to ignore
  const workflowMessage =
    intentWorkflowId && !isLoading && view !== 'voice'
      ? getWorkflowNavbarMessage({
          isAutoflowWorkflow,
          isClassicWorkflowsDeprecated: featureFlags.includes(
            'classic_workflows_deprecated',
          ),
          mode,
        })
      : null;

  const shouldDisplayMultilingualIcon =
    !isEmailBuilderView || isEmailBuilderView;
  const shouldShowNotification =
    isNotificationButtonRoute(pathname) &&
    doesNotificationButtonRouteMeetCondition(pathname);
  const isOrchestratorDetailPage = pathname.startsWith('/orchestrator/');
  const isOrchestratorPage =
    pathname === Routes.ORCHESTRATOR || isOrchestratorDetailPage;

  const primaryHeader = useMemo(() => {
    if (isRevampedWorkflowBuilderEditPage) {
      return <WorkflowBuilderEditHeader isDarkMode={isDarkMode} isFromNavbar />;
    } else if (isOrchestratorDetailPage) {
      return <OrchestratorHeader isDarkMode={isDarkMode} isFromNavbar />;
    } else {
      return (
        <StyledTabContainer>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Typography
                color={isDarkMode ? 'white' : undefined}
                component='div'
                noWrap
                variant='font24'
              >
                {currentTabOverride ?? tabName}
              </Typography>
            </Box>
            {tabs && (
              <>
                <Box
                  sx={{
                    bgcolor: isDarkMode
                      ? 'rgba(255, 255, 255, 0.40)'
                      : palette.colors.slate[200],

                    flexShrink: 0,
                    height: '32px',
                    mx: 2,
                    width: '1px',
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>{tabs}</Box>
              </>
            )}
            {isInSimulationsDetailPage && (
              <Box
                sx={{
                  ml: 1,
                }}
              >
                <IconButton
                  aria-label={t('navbar.menu')}
                  onClick={handleMenuClick}
                  variant='ghost'
                >
                  <IconDotsVertical size={24} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom',
                  }}
                  onClose={() => setAnchorEl(null)}
                  open={Boolean(anchorEl)}
                  transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      if (onRenameModel) {
                        onRenameModel();
                      }
                      handleMenuClose();
                    }}
                  >
                    {t('navbar.renameModel')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (onDeleteModel) {
                        onDeleteModel();
                      }
                      handleMenuClose();
                    }}
                  >
                    {t('navbar.deleteModel')}
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </StyledTabContainer>
      );
    }
  }, [
    isRevampedWorkflowBuilderEditPage,
    isOrchestratorDetailPage,
    isDarkMode,
    currentTabOverride,
    tabName,
    tabs,
    isInSimulationsDetailPage,
    anchorEl,
    onRenameModel,
    onDeleteModel,
    palette.colors.slate,
    t,
  ]);

  return (
    <>
      {isWorkflowBuilderEditPage && isWorkflowInActiveAbTest && (
        <AbTestTopBanner activeAbTest={activeAbTest} />
      )}
      <StyledNav
        className={NAVBAR_V2_CLASS}
        hideBorder={hideBorder}
        isDarkMode={isDarkMode}
        isNewDarkMode={isNewDarkMode}
        top={top}
      >
        {onGoBack && <BackButton onClick={onGoBack} src={BackIcon} />}
        {primaryHeader}
        {shouldShowNotification && (
          <Box
            sx={{
              mr: 2,
            }}
          >
            <IconButton
              aria-label={t('navbar.notification')}
              onClick={() =>
                dispatch(setNavbarNotificationToggle(pathname as Routes))
              }
              variant='ghost'
            >
              {isNotificationVisible ? (
                <img src={iconPurpleBulb} />
              ) : (
                <img src={iconBulb} />
              )}
            </IconButton>
          </Box>
        )}
        {Routes.WORKFLOW_BUILDER_EDIT === pathname &&
          workflowMessage &&
          !isEmailBuilderView &&
          isCurrentWorkflowActive && (
            <NavbarMessage isDarkMode={isDarkMode}>
              {workflowMessage}
            </NavbarMessage>
          )}
        {children}
        <Box
          sx={{
            display: 'flex',
            flex: '0 0 auto',
            justifyContent: 'flex-end',
          }}
        >
          {isOrchestratorPage ? (
            <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
              <Tooltip tooltipContent='Context variables'>
                <Button
                  onClick={() =>
                    dispatch(
                      setGlobalWorkflowBuilderOptions({ isCvDrawerOpen: true }),
                    )
                  }
                  startIcon={
                    <IconVariable
                      color={
                        isDarkMode
                          ? palette.colors.white
                          : palette.colors.grey[700]
                      }
                      size={20}
                    />
                  }
                  variant='ghost'
                />
              </Tooltip>
            </Box>
          ) : isRevampedWorkflowBuilderPages || isRevampedActionBuilderPages ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mr: 1,
              }}
            >
              {
                <Tooltip tooltipContent='Brands'>
                  <Button
                    onClick={() =>
                      dispatch(
                        setGlobalWorkflowBuilderOptions({
                          isBrandManagementDrawerOpen: true,
                        }),
                      )
                    }
                    startIcon={
                      <IconUsers
                        color={
                          isDarkMode
                            ? palette.colors.white
                            : palette.colors.grey[700]
                        }
                        size={20}
                      />
                    }
                    variant='ghost'
                  />
                </Tooltip>
              }
              <Tooltip tooltipContent='Context variables'>
                <Button
                  onClick={() =>
                    dispatch(
                      setGlobalWorkflowBuilderOptions({ isCvDrawerOpen: true }),
                    )
                  }
                  startIcon={
                    <IconVariable
                      color={
                        isDarkMode
                          ? palette.colors.white
                          : palette.colors.grey[700]
                      }
                      size={20}
                    />
                  }
                  variant='ghost'
                />
              </Tooltip>
              <CustomEventsButton isDarkMode={isDarkMode} />
              {shouldDisplayMultilingualIcon && (
                <Tooltip tooltipContent='Translations'>
                  <Button
                    onClick={() => {
                      dispatch(setStepLevelTranslationsId(''));
                      dispatch(
                        setGlobalWorkflowBuilderOptions({
                          isTranslationDrawerOpen: true,
                        }),
                      );
                    }}
                    startIcon={
                      <IconWorld
                        color={
                          isDarkMode
                            ? palette.colors.white
                            : palette.colors.grey[700]
                        }
                        size={20}
                      />
                    }
                    variant='ghost'
                  />
                </Tooltip>
              )}
            </Box>
          ) : null}
          {isSolveInsightsPages && (
            <Box
              sx={{
                marginRight: 1,
              }}
            >
              <CustomEventsButton isDarkMode={isDarkMode} />
            </Box>
          )}
          {
            // Render the button and the support widget only if not on the workflow
            // builder preview page, because 2 widgets can't coexist on the same
            // page (due to `postMessage()` conflicts between widget iframes and the
            // parent page):
            !pathname.startsWith('/workflow-builder-preview') && (
              <WidgetLaunchButton
                color={
                  isDarkMode ? palette.colors.white : palette.colors.grey[700]
                }
              />
            )
          }
          {isAIStudioNavbarUxEnabled && isAIStudioEnabled && (
            <Tooltip tooltipContent={t('sidebar.aiStudio')}>
              <AIStudioIconButton
                ariaLabel={t('sidebar.aiStudio')}
                circleSize={24}
                isLoading={isAIStudioLoading}
                onClick={() => openAIStudio({ entryMethod: 'navbar_click' })}
                padding={4}
                sx={{ marginLeft: '8px' }}
              />
            </Tooltip>
          )}
          <div ref={ref}>
            <AvatarButton
              aria-label={t('navbar.accountSetting')}
              onClick={toggleModalVisibility}
              sx={{ marginLeft: '8px' }}
              type='button'
            >
              <Avatar
                backgroundColor={palette.colors.primary[200]}
                color={palette.colors.primary[700]}
                variant='md'
              >
                {email.charAt(0).toUpperCase()}
              </Avatar>
            </AvatarButton>
            <div className='Hidden'>{email}</div>
            {modalVisibility && (
              <ControlMenu
                handleModalVisibility={toggleModalVisibility}
                role={role}
              />
            )}
          </div>
        </Box>
      </StyledNav>
    </>
  );
};

interface NavbarMessageProps {
  children: React.ReactNode;
  isDarkMode?: boolean;
}

const NavbarMessage = ({
  children,
  isDarkMode = false,
}: NavbarMessageProps) => {
  return (
    <Box
      sx={theme => ({
        // TODO color literal should come from design system
        backgroundColor: 'rgba(254, 177, 155, 0.20)',
        border: '1px solid' + theme.palette.colors.red[500],
        borderRadius: '5px',
        color: isDarkMode
          ? theme.palette.colors.white
          : theme.palette.colors.grey[700],
        minWidth: '50px',
        overflow: 'hidden',
        padding: '3px 16px',
        textOverflow: 'ellipsis',
      })}
    >
      <Typography noWrap variant='font14'>
        {children}
      </Typography>
    </Box>
  );
};

const StyledTabContainer = styled('h1')`
  flex: 1;
  margin: 0;
  text-transform: capitalize;
  @media only screen and ${breakpoints.mobile} {
    text-align: center;
  }
`;

const StyledNav = styled('nav', {
  shouldForwardProp: prop =>
    isPropValid(String(prop)) &&
    prop !== 'isDarkMode' &&
    prop !== 'isNewDarkMode' &&
    prop !== 'hideBorder',
})<{
  hideBorder?: boolean;
  isDarkMode: boolean;
  isNewDarkMode: boolean;
  top?: string;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 16px 40px;
  background-color: ${props =>
    props.isNewDarkMode
      ? 'transparent'
      : props.isDarkMode
        ? props.theme.palette.colors.primary[800]
        : props.theme.palette.background.default};
  border-bottom: ${props =>
    props.hideBorder || props.isNewDarkMode
      ? undefined
      : `1px solid ${props.theme.palette.colors.slate[200]}`};
  position: sticky;
  isolation: isolate;
  top: ${props => props.top || '0px'};
  z-index: 1200;
  transition: ${props =>
    props.theme.transitions.create(['background-color'], { duration: 100 })};
  & .MuiButton-root {
    &:hover {
      /* overrides button hover styles. Once dark theme variants are added please remove this */
      background-color: ${props =>
        props.isDarkMode
          ? lighten(props.theme.palette.colors.brand.darkGrey, 0.1)
          : undefined};
    }
  }
`;

const AvatarButton = styled('button')`
  cursor: pointer;
  padding: 0;
`;

const BackButton = styled('img')`
  margin-right: 20px;
  cursor: pointer;
`;

BackButton.defaultProps = {
  'aria-label': i18n.t('app:navbar.back'),
  role: 'button',
};

export default Navbar;
